import type { AudienceSelection, CourseworkGroup } from "@/lib/api/courseworkPolicy";

type Learner = { enrollment_id: string; display_name: string; status?: string };

const list = (names: string[]) => names.length < 3 ? names.join(" and ") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

/** Named learners, or a count when any name is unread. */
function learners(ids: string[], roster: Learner[] | undefined, fallback: string): string {
  const names = ids.map(id => roster?.find(row => row.enrollment_id === id)?.display_name);
  if (!names.length) return fallback;
  if (names.some(name => !name)) return `${ids.length} ${ids.length === 1 ? "learner" : "learners"}`;
  return names.length === 1 ? names[0]! : `${names.length} learners: ${list(names as string[])}`;
}

/**
 * Who a publication reaches, in the teacher's words: the audience they chose,
 * resolved to names where the course's learner list has been read.
 */
export function audienceWords(audience: AudienceSelection | null | undefined, roster: Learner[] | undefined, groups: CourseworkGroup[] | undefined): { who: string; after: string } {
  if (!audience) return { who: "the course's enrolled learners", after: "" };
  if (audience.mode === "section") return { who: "the whole section", after: "Learners who enrol later receive it too." };
  if (audience.mode === "selected") {
    const who = learners(audience.enrollment_ids, roster, "the selected learners");
    return { who: audience.enrollment_ids.length === 1 ? `${who} only` : who, after: "" };
  }
  const group = groups?.find(row => row.id === audience.group_id && row.revision === audience.group_revision);
  const ids = audience.enrollment_ids.length ? audience.enrollment_ids
    : group?.enrollment_ids.filter(id => roster?.find(row => row.enrollment_id === id)?.status === "enrolled");
  const name = audience.group_name ?? group?.name ?? "the chosen group";
  const members = ids && roster ? learners(ids, roster, "") : "";
  return { who: members ? `${name} (${members})` : name, after: "Later changes to the group don't change these recipients." };
}
